import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiClient, CLAIMS_TIMEOUT_MS, RECAPTCHA_TOKEN_HEADER } from '@/lib/api-client';
import { useActiveSearch, type PolicySearchCriteria } from '@/lib/active-search';
import { QUERY_KEYS } from '@/lib/query-keys';
import { executeRecaptcha } from '@/lib/recaptcha';
import { useSessionStore } from '@/store/session-store';
import type { ApiError } from '@/types/api.types';
import type { ClaimsResult } from '../types/claims.types';

/**
 * Claims for the active search (US-009).
 *
 * A separate query from the policy one on purpose: the Claims API going down must leave the
 * Policyholder Information section rendered (US-011 AC-005). Keyed on the criteria, so a new search
 * replaces the previous policy's claims outright (US-013 AC-002).
 */
export function useClaims(): UseQueryResult<ClaimsResult, ApiError> {
  const { data } = useActiveSearch();
  const criteria = data ?? null;
  const providerId = useSessionStore((state) => state.providerId);

  return useQuery<ClaimsResult, ApiError>({
    queryKey: [...QUERY_KEYS.claims, providerId, criteria],
    queryFn: () => {
      if (criteria === null || providerId === null) {
        // Unreachable while `enabled` is false; the guard is what keeps the types honest.
        throw new Error('No active policy search.');
      }

      return fetchClaims(providerId, criteria);
    },
    enabled: criteria !== null && providerId !== null,
    staleTime: 5 * 60_000,
  });
}

/**
 * The BF API scopes claims by provider, so the id the session was granted under travels with the
 * request. portal-backend cannot yet prove it belongs to this caller — see the SCAFFOLD note on
 * ClaimsSearchRequest; the portal issues no server-side session.
 */
async function fetchClaims(
  providerId: string,
  criteria: PolicySearchCriteria,
): Promise<ClaimsResult> {
  const recaptchaToken = await executeRecaptcha('policy_search');
  const response = await apiClient.post<ClaimsResult>(
    '/api/policies/claims',
    { providerId, ...criteria },
    {
      headers: { [RECAPTCHA_TOKEN_HEADER]: recaptchaToken },
      // A large policy's claims legitimately take longer than the client's default allows; see
      // CLAIMS_TIMEOUT_MS. Only this call gets the longer rope.
      timeout: CLAIMS_TIMEOUT_MS,
    },
  );

  return response.data;
}
